'use client';

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { MigrationStatus as MigrationStatusType } from '../store/platformSlice';

interface MigrationStatusProps {
  status: MigrationStatusType;
  progress?: number;
  entityType?: string;
  className?: string;
  showProgress?: boolean;
}

export const MigrationStatus: React.FC<MigrationStatusProps> = ({ 
  status, 
  progress, 
  entityType,
  className = '',
  showProgress = false
}) => {
  const statusStyles = {
    pending: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
    in_progress: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    completed: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    failed: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
  };

  const statusLabels = {
    pending: 'Pending',
    in_progress: 'Migrating',
    completed: 'Completed',
    failed: 'Failed'
  };

  const statusIcons = {
    pending: '⏳',
    in_progress: '🔄',
    completed: '✅',
    failed: '❌'
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Badge 
        variant="outline"
        className={cn(
          "inline-flex items-center gap-1 text-xs font-medium",
          statusStyles[status]
        )}
      >
        <span>{statusIcons[status]}</span>
        {statusLabels[status]}
        {entityType && ` ${entityType}`}
      </Badge>
      
      {showProgress && status === 'in_progress' && progress !== undefined && (
        <div className="flex items-center gap-2">
          <Progress 
            value={Math.min(100, Math.max(0, progress))} 
            className="w-16 h-2"
          />
          <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
            {Math.round(progress || 0)}%
          </span>
        </div>
      )}
    </div>
  );
};

interface MigrationProgressBarProps {
  progress: number;
  total?: number;
  label?: string;
  className?: string;
}

export const MigrationProgressBar: React.FC<MigrationProgressBarProps> = ({
  progress,
  total = 100,
  label,
  className = ''
}) => {
  const percentage = total > 0 ? (progress / total) * 100 : 0;
  
  return (
    <div className={cn("w-full", className)}>
      {label && (
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-foreground">{label}</span>
          <span className="text-muted-foreground">
            {progress}{total !== 100 ? `/${total}` : '%'}
          </span>
        </div>
      )}
      <Progress 
        value={Math.min(100, Math.max(0, percentage))} 
        className="h-2"
      />
    </div>
  );
};

export default MigrationStatus;
